import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';

/**
 * True when a CANCELLED booking still carries an open, non-cancellable position — in
 * practice the retained ticket insurance ("Ticketversicherung") that stays in when
 * everything else is cancelled: an active line item with an unpaid offline payment
 * reference. Any other unpaid case belongs to the no-show / forgotten-payment flows, so
 * this action is deliberately limited to cancelled bookings.
 */
export function hasOpenRetainedInsurance(booking) {
  if (!booking || booking.status !== 'canceled' || booking.fullyPaid) return false;
  // Invoice already issued → keep the action visible (shows "bereits gesendet").
  if (booking.unpaidBookingInvoiceId) return true;
  const lineItems = Array.isArray(booking.lineItems) ? booking.lineItems : [];
  return lineItems.some(
    li =>
      li &&
      li.status === 'active' &&
      (li.count || 0) > 0 &&
      Array.isArray(li.paymentReferences) &&
      li.paymentReferences.some(
        ref => ref && ref.paymentType === 'offline' && (ref.count || 0) > 0 && !ref.isPaid
      )
  );
}

/**
 * Action for a CANCELLED booking that still carries an open, non-cancellable ticket
 * insurance ("Ticketversicherung"). Sends a proper invoice email (PDF + online payment
 * link) and feeds the open receivable into the Creditreform ("krefo") collection workflow.
 */
function UnpaidBookingInvoiceButton({
  booking,
  onAfterSend,
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { sendUnpaidBookingInvoice } = useInternalBooking();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id) return false;
    if (!hasOpenRetainedInsurance(booking)) return false;
    return authCtx.checkRightAny('internalBooking:special:sendUnpaidBookingInvoice');
  }, [authCtx, booking]);

  if (!canShow) return null;

  const alreadySent = Boolean(booking?.unpaidBookingInvoiceId);
  const alreadySkipped = Boolean(booking?.unpaidBookingInvoiceSkippedAt);
  const hasMail = Boolean(booking?.mail);

  const handleOpenConfirm = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || alreadySent || alreadySkipped || !hasMail) return;
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isSubmitting) return;
    setIsConfirmOpen(false);
  };

  const handleConfirmSend = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || alreadySent || alreadySkipped || !hasMail) return;

    try {
      setIsSubmitting(true);
      await sendUnpaidBookingInvoice(booking._id);
      await onAfterSend?.();
      setIsConfirmOpen(false);
    } catch (err) {
      const message =
        err?.data?.message || err?.message || 'Versand der Rechnung fehlgeschlagen.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  let label = 'Offene Rechnung per Mail senden';
  if (!hasMail) label = 'Keine E-Mail-Adresse';
  if (alreadySkipped) label = 'Ohne Rechnung erledigt';
  if (alreadySent) label = 'Rechnung bereits gesendet';

  const primaryDisabled = isSubmitting || !hasMail || alreadySent || alreadySkipped;

  const sendButton = (
    <Button
      size={size}
      variant={variant}
      color={alreadySent || alreadySkipped ? 'success' : 'warning'}
      fullWidth={fullWidth}
      disabled={primaryDisabled}
      onClick={handleOpenConfirm}
      data-ignore-card-click
      sx={{ whiteSpace: 'nowrap', ...sx }}
    >
      {label}
    </Button>
  );

  return (
    <>
      {!hasMail ? (
        <Tooltip title='Für diese Buchung ist keine E-Mail-Adresse hinterlegt.'>
          <span
            style={fullWidth ? { display: 'block', width: '100%' } : { display: 'inline-block' }}
          >
            {sendButton}
          </span>
        </Tooltip>
      ) : (
        sendButton
      )}

      <Dialog open={isConfirmOpen} onClose={handleCloseConfirm} fullWidth maxWidth='sm'>
        <DialogTitle>Offene Rechnung an den Kunden senden?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' paragraph>
            Für diese Buchung ist noch ein Betrag offen geblieben (z. B. eine nicht
            stornierbare <strong>Ticketversicherung</strong> nach einer Stornierung). Der
            Kunde erhält eine offizielle Rechnung per E-Mail an{' '}
            <strong>{booking?.mail || ''}</strong>, inklusive PDF-Anhang und
            Online-Zahlungslink.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Bleibt die Zahlung aus, wird die Forderung über den Mahn-/Creditreform-Prozess
            in der Rechnungsansicht weiterverfolgt. Prüfe vorher, ob der Betrag nicht doch
            schon kassiert wurde – in diesem Fall bitte stattdessen "Als bezahlt markieren".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmSend}
            disabled={primaryDisabled}
            color='warning'
            variant='contained'
          >
            Rechnung jetzt senden
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UnpaidBookingInvoiceButton;
